import { v4 as uuid } from "uuid";
import { STARTER_EMOTES } from "./season";

export const START_XP = 1000;

export const MAP_WIDTH_TILES = 48;
export const MAP_HEIGHT_TILES = 48;
export const TILE_SIZE = 32;

export const PLAYER_MOVE_SPEED = 5;
export const PLAYER_SPRINT_MULTIPLIER = 1.7;
export const PLAYER_UNARMED_ATTACK_RANGE = 48;
export const PLAYER_UNARMED_ATTACK_INTERVAL = 1000;
export const PLAYER_UNARMED_ATTACK_DAMAGE = 30;

export const EXPLOSION_DAMAGE: number = 60;
export const MELEE_RANGE_THRESHOLD = 48; // pixels

export const SELL_PRICE_FACTOR = 0.25; // Players get 25% of the item price when selling to merchants

export function calcSellPrice(itemPrice: number): number {
  return Math.floor((typeof itemPrice === "number" ? itemPrice : 0) * SELL_PRICE_FACTOR);
}

// PlayerData type for consistent player data usage
export type PlayerData = {
  name: string;
  trophies: number;
  inventory: Item[];
  equipment: Partial<Record<EquipType, Item>>;
  recipes: number[];
  storage: Item[];
  actions: Item[];
  credits: number;
  accountMail: string;
  activeSkin?: string;
  killStreak: number;
  dailyExtractions: number;
  lastDailyResetDate?: string; // ISO date string (YYYY-MM-DD)
  limbs: Record<LimbType, LimbStatus>;
  mailVerified: boolean;
  admin: boolean;
  stations: BaseStation[];
  merchants: Merchant[];
  nrExtractions: number; // Track total successful extractions

  seasonXp: number; // cumulative Season XP total; level/progress derived via getSeasonLevel()
  seasonPassOwned: boolean;
  // Levels whose reward has actually been claimed (see SeasonManager.claimReward) — a level
  // being reached (seasonXp) no longer implies its reward has been granted.
  claimedSeasonLevels: { free: number[]; premium: number[] };

  unlockedEmotes: string[]; // EmoticonKey[]; starts with the 4 STARTER_EMOTES
  unlockedSprays: number[]; // ids, from sprays.json catalog
  unlockedProfileIcons: number[]; // ids, from profile-icons.json catalog
  unlockedTitles: number[]; // ids, from titles.json catalog
  unlockedHats: number[]; // ids, from hats.json catalog
  unlockedBorders: number[]; // ids, from borders.json catalog
  unlockedSkins: string[]; // ids, from season-skins.json catalog (season-reward skins only)

  equippedProfileIcon: number; // 0 = none
  equippedTitle: number; // 0 = none
  equippedHat?: number; // undefined = none
  equippedBorder: number; // 0 = none
};

export type FriendInfo = {
  accountId: number;
  name: string;
  killStreak: number;
  nrExtractions: number;
  isOnline: boolean;
};

export type FriendRequest = {
  accountId: number;
  name: string;
  killStreak: number;
  nrExtractions: number;
};

export type BaseStation = {
  name: string;
  level: number;
};

export type StationLevelInfo = {
  level: number;
  recipes: number[];
  materials: { itemId: number; quantity: number }[];
};

export type StationInfo = {
  name: string;
  levels: StationLevelInfo[];
};

export type Merchant = { 
  id: number, 
  level: number 
};

export const BASE_STATIONS_DEFAULT: BaseStation[] = [
  { name: "ammo-station", level: 1 },
  { name: "weapon-station", level: 1 },
  { name: "support-station", level: 1 },
];

export const MERCHANTS_DEFAULT: Merchant[] = [
  { id: 1, level: 1 },
  { id: 2, level: 1 },
  { id: 3, level: 1 },
  { id: 4, level: 1 },
];

export const RECIPES_DEFAULT: number[] = [
  2,22,122
];

// Auto-installs any Recipe-type items into playerData.recipes and strips them from the
// returned list. Recipes the player already knows are dropped as duplicates rather than kept.
export function autoInstallRecipes(playerData: PlayerData, items: Item[]): Item[] {
  if (!Array.isArray(playerData.recipes)) {
    playerData.recipes = [];
  }

  return items.filter((item) => {
    if (item.definition.type !== ItemType.Recipe) return true;

    const targetItemId = item.definition.stats?.targetItemId;
    if (targetItemId && !playerData.recipes.includes(targetItemId)) {
      playerData.recipes.push(targetItemId);
    }
    return false;
  });
}

export type LootBoxInfo = {
  items: Item[];
  isAirDrop?: boolean;
  x: number;
  y: number;
};

export type LootBoxInfoDB = {
  items: ItemDB[];
  isAirDrop?: boolean;
  x: number;
  y: number;
};

export type LootBoxSpriteInfo = {
  x: number;
  y: number;
  spriteSheet: string;
  isAirDrop: boolean;
  spriteX: number;
  spriteY: number;
};

export type MerchantTradeItem = {
  id: number;
  count?: number;
};

export type MerchantLevel = {
  fetchItems: MerchantTradeItem[];
  sellItems: MerchantTradeItem[];
};

export type MerchantInfo = {
  id: number;
  name: string;
  spritesheet: string;
  spriteX: number;
  spriteY: number;
  x: number;
  y: number;
  levels: MerchantLevel[];
};

export enum SlotType {
  EQUIP = "EQUIP",
  INVENTORY = "INVENTORY",
  ACTION = "ACTION",
  LOOT = "LOOT",
  STORAGE = "STORAGE",
}
export enum ItemType {
  Equipment = "Equipment",
  Consumable = "Consumable",
  Ammo = "Ammo",
  Throwable = "Throwable",
  Material = "Material",
  Recipe = "Recipe",
}

export enum EquipType {
  Weapon = "Weapon",
  Head = "Head",
  Chest = "Chest",
  Legs = "Legs",
  Feet = "Feet",
  Back = "Back",
  Action = "Action",
}

export enum LimbType {
  Head = "Head",
  Chest = "Chest",
  Legs = "Legs",
  Feet = "Feet",
}

export enum LimbStatus {
  HEALTHY = "HEALTHY",
  BLEEDING = "BLEEDING",
}

export const LIMBS_DEFAULT_VALUES: Record<LimbType, LimbStatus> = {
  [LimbType.Head]: LimbStatus.HEALTHY,
  [LimbType.Chest]: LimbStatus.HEALTHY,
  [LimbType.Legs]: LimbStatus.HEALTHY,
  [LimbType.Feet]: LimbStatus.HEALTHY,
};

export type ItemDefinition = {
  id: number;
  name: string;
  type: ItemType;
  equipType?: EquipType;
  stats: Record<string, number>;
  spriteSheet: string;
  spriteX: number;
  spriteY: number;
  level: number;
  price: number;
  containers?: Array<{
    name: string;
    dropProbability: number;
  }>;
  sfx?: string;
};

export type StatModifier = {
  stat: string;
  value: number;
};

export const MAX_STACK = 10;

export type Item = {
  id: string; // Unique identifier for this specific item instance
  definition: ItemDefinition; // Store original definition for consistency
  modifiers: ItemDefinition[]; // item ids representing modifiers
  currentAmmo?: number; // For weapons that use ammo
  currentArmor?: number; // For armor items: current durability (in-memory only, not persisted)
  count?: number; // For stackable (non-Equipment) items
};

export type ItemDB = {
  id?: string;
  definitionId: number;
  modifiers: number[];
  currentAmmo?: number;
  count?: number;
};

export type Recipe = {
  itemId: number;
  materials: { itemId: number; quantity: number }[];
  enabled?: boolean;
};

export type CraftingStation = {
  name: string;
  levels: [
    {
      level: number;
      recipes: number[];
      materials: { itemId: number; quantity: number }[];
    }
  ]
}

export type EnemyDefinition = {
  name: string;
  type: string;
  sprite: string;
  sfx?: string;
  moveInterval: number;
  aggroRange: number;
  attackRange: number;
  damage: number;
  health: number;
  attackInterval: number;
  attackAccuracy: number;
  behaviours: string[];
  size?: number;
  probability?: number;
  spriteSize?: number; // Sprite frame size in pixels (defaults to 32),
  xp: number;
  weapon: number[] | null;
};

export type EnemyState = {
  id: string;
  definition: EnemyDefinition;
  name: string;
  x: number;
  y: number;
  health: number;
  dead: boolean;
  showAggro: boolean;
  behavior?: string;
  weapon?: Item;
  reloading?: boolean;
  reloadProgress?: number;
  activeBehaviorName?: string;
  skin?: string;
  hat?: number;
  timestamp: number;
};

export const getLootBoxSpriteInfo = (
  lootBox: LootBoxInfo,
): LootBoxSpriteInfo => {
  return {
    x: lootBox.x,
    y: lootBox.y,
    spriteSheet: lootBox.isAirDrop
      ? "airdrop-box"
      : lootBox.items.length > 1 || lootBox.items.length === 0
        ? "items"
        : lootBox.items[0].definition.spriteSheet,
    isAirDrop: lootBox.isAirDrop || false,
    spriteX: lootBox.isAirDrop
      ? 1
      : lootBox.items.length > 1 || lootBox.items.length === 0
        ? 6
        : lootBox.items[0].definition.spriteX,
    spriteY: lootBox.isAirDrop
      ? 1
      : lootBox.items.length > 1 || lootBox.items.length === 0
        ? 12
        : lootBox.items[0].definition.spriteY,
  };
};

export const getItemStat = (item: Item, statType: string): number => {
  if (item.definition.stats && statType in item.definition.stats) {
    if (
      item.modifiers.length > 0 &&
      item.modifiers.some((mod) => mod.stats && statType in mod.stats)
    ) {
      return (
        item.definition.stats[statType] +
        item.modifiers.reduce((total, mod) => {
          if (mod.stats && statType in mod.stats) {
            return total + mod.stats[statType];
          }
          return total;
        }, 0)
      );
    }
    return item.definition.stats[statType];
  }
  return 0;
};

/**
 * Computes the total max armor from all equipped items' armor stats.
 */
export const computeMaxArmor = (equipment: Partial<Record<EquipType, Item>>): number => {
  let total = 0;
  for (const item of Object.values(equipment)) {
    if (item) {
      total += getItemStat(item, "armor");
    }
  }
  return total;
};

/**
 * Computes the total current armor across equipped items.
 * If an item's currentArmor is undefined it is treated as full (its max armor stat).
 */
export const computeCurrentArmor = (equipment: Partial<Record<EquipType, Item>>): number => {
  let total = 0;
  for (const item of Object.values(equipment)) {
    if (item) {
      const max = getItemStat(item, "armor");
      total += item.currentArmor !== undefined ? item.currentArmor : max;
    }
  }
  return total;
};

/**
 * Distributes armor damage proportionally across equipped armor items,
 * updating each item's currentArmor value.
 */
export const distributeArmorDamage = (equipment: Partial<Record<EquipType, Item>>, totalDamage: number): void => {
  const armorItems = Object.values(equipment).filter(
    (item): item is Item => !!item && getItemStat(item, "armor") > 0,
  );
  if (armorItems.length === 0) return;

  const totalCurrentArmor = computeCurrentArmor(equipment);
  if (totalCurrentArmor <= 0) return;

  let remaining = totalDamage;
  for (let i = 0; i < armorItems.length; i++) {
    const item = armorItems[i];
    const current = item.currentArmor !== undefined ? item.currentArmor : getItemStat(item, "armor");
    if (current <= 0) continue;

    const isLast = i === armorItems.length - 1;
    const share = isLast
      ? remaining
      : Math.min(Math.round(totalDamage * (current / totalCurrentArmor)), current, remaining);
    const applied = Math.min(share, current);
    item.currentArmor = current - applied;
    remaining -= applied;
    if (remaining <= 0) break;
  }
};

export interface ArmorItemData {
  current: number;
  max: number;
  color?: string;
}

export const getArmorItemsFromEquipment = (equipment: Partial<Record<EquipType, Item>>): ArmorItemData[] => {
  return [EquipType.Head, EquipType.Chest, EquipType.Legs, EquipType.Feet]
    .map((slot) => equipment[slot])
    .filter((item): item is Item => !!item && getItemStat(item, "armor") > 0)
    .map((item) => ({
      current: item.currentArmor !== undefined ? item.currentArmor : getItemStat(item, "armor"),
      max: getItemStat(item, "armor"),
      color: (item.definition.stats as any)["color"] as string | undefined,
    }));
};

// Derives the preloaded texture key (e.g. "hat-3") for an equipped hats.json id
// without needing an async catalog fetch.
export const getHatSpriteKey = (equippedHat?: number): string | undefined => {
  if (!equippedHat) return undefined;
  return `hat-${equippedHat}`;
};

export const getItemToDB = (item: Item): ItemDB => {
  return {
    id: item.id,
    definitionId: item.definition.id,
    modifiers: item.modifiers.map((mod) => mod.id),
    currentAmmo: item.currentAmmo,
    count: item.count,
  };
};

export const getDBToItem = (
  itemDB: ItemDB | number,
  itemDefs: ItemDefinition[],
): Item => {
  if (typeof itemDB === "number") {
    // Old format where itemDB is just the definition ID, can be removed in future once all items are saved in new format
    const definition = itemDefs.find((def) => def.id === itemDB);
    if (!definition) {
      throw new Error(`Item definition not found for id: ${itemDB}`);
    }

    return {
      id: uuid(), // Generate new ID if not provided
      definition, // Use the full definition fetched from itemDefs
      modifiers: [],
      currentAmmo: definition.stats?.ammo ?? 0,
    };
  } else {
    const definition = itemDefs.find((def) => def.id === itemDB.definitionId);
    if (!definition) {
      throw new Error(`Item definition not found for id: ${itemDB.id}`);
    }
    return {
      id: itemDB.id ?? uuid(), // Generate new ID if not provided
      definition, // Use the full definition fetched from itemDefs
      modifiers: itemDB.modifiers.map((modId) => {
        const modDef = itemDefs.find((def) => def.id === modId);
        if (!modDef) {
          throw new Error(`Modifier definition not found for id: ${modId}`);
        }
        return modDef;
      }),
      currentAmmo: itemDB.currentAmmo,
      count: itemDB.count,
    };
  }
};

export const createItem = (itemDef: ItemDefinition): Item => {
  const itemUId = uuid(); // Generate a unique ID for this item instance

  return {
    id: itemUId,
    definition: itemDef,
    modifiers: [],
    currentAmmo:
      itemDef.type === ItemType.Equipment &&
      itemDef.equipType === EquipType.Weapon
        ? (itemDef.stats?.ammo ?? 0)
        : undefined,
    count: itemDef.type !== ItemType.Equipment ? 1 : undefined,
  };
};

export type WheelPrize = {
  type: "item" | "xp" | "none";
  itemDef?: ItemDefinition;
  xpAmount?: number;
};

export type LeaderboardEntry = {
  rank: number;
  name: string;
  trophies: number;
  isCurrentPlayer: boolean;
};

export const PLAYER_DATA_DEFAULT: PlayerData = {
  name: "Guest player",
  trophies: START_XP,
  inventory: [],
  equipment: {},
  recipes: RECIPES_DEFAULT,
  storage: [],
  actions: [],
  credits: 0,
  accountMail: "",
  killStreak: 0,
  dailyExtractions: 0,
  lastDailyResetDate: undefined,
  limbs: LIMBS_DEFAULT_VALUES,
  mailVerified: false,
  admin: false,
  stations: BASE_STATIONS_DEFAULT,
  merchants: MERCHANTS_DEFAULT,
  nrExtractions: 0,
  seasonXp: 0,
  seasonPassOwned: false,
  claimedSeasonLevels: { free: [], premium: [] },
  unlockedEmotes: [...STARTER_EMOTES],
  unlockedSprays: [],
  unlockedProfileIcons: [],
  unlockedTitles: [],
  unlockedHats: [],
  unlockedBorders: [],
  unlockedSkins: [],
  equippedProfileIcon: 0,
  equippedTitle: 0,
  equippedHat: undefined,
  equippedBorder: 0,
};